"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";

const CAMPAIGN = "organic_blog_conversion_v2";

interface BlogProductCTAProps {
  slug: string;
  placement: "inline_after_intro" | "footer";
  headline?: string;
}

export default function BlogProductCTA({
  slug,
  placement,
  headline,
}: BlogProductCTAProps) {
  const posthog = usePostHog();
  const containerRef = useRef<HTMLElement>(null);
  const capturedViewRef = useRef(false);
  const isInline = placement === "inline_after_intro";
  const destination = `/upload?utm_source=blog&utm_medium=product_cta&utm_campaign=${CAMPAIGN}&utm_content=${encodeURIComponent(slug)}`;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const captureView = () => {
      if (!posthog || capturedViewRef.current) return;

      capturedViewRef.current = true;
      posthog.capture("blog_product_cta_viewed", {
        blog_slug: slug,
        placement,
        campaign: CAMPAIGN,
        destination: "/upload",
      });
    };

    if (!("IntersectionObserver" in window)) {
      captureView();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          captureView();
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [placement, posthog, slug]);

  const handleClick = () => {
    posthog?.capture("blog_product_cta_clicked", {
      blog_slug: slug,
      placement,
      campaign: CAMPAIGN,
      destination: "/upload",
    });
  };

  return (
    <aside
      ref={containerRef}
      className={
        isInline
          ? "my-8 overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-7"
          : "rounded-xl bg-primary/5 p-6 text-center sm:p-8"
      }
      aria-label="Try SlideToAnki"
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
        Turn reading into recall
      </p>
      <h2
        className={
          isInline
            ? "text-xl font-serif font-semibold text-foreground sm:text-2xl"
            : "text-xl font-serif font-semibold text-foreground"
        }
      >
        {headline ?? "Ready to save hours on flashcard creation?"}
      </h2>
      <p className={isInline ? "mt-3 max-w-2xl text-foreground/75" : "mx-auto mt-2 max-w-xl text-muted"}>
        Upload a lecture PDF or PowerPoint, check the generated cards, edit or reject what needs changing, and download your deck for Anki.
      </p>
      <Link
        href={destination}
        onClick={handleClick}
        className="ui-button ui-button--primary mt-5"
      >
        Turn one lecture into cards
      </Link>
      <p className="mt-3 text-xs text-muted">Free to try · PDF and PowerPoint</p>
    </aside>
  );
}
